/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import { v4 as uuid } from "uuid";
import {
  KnowledgeComponentRelevance,
  LearnerRecord,
} from "@ictlearningsciences/pal3-core/types";
import { getFormattedDate } from "@ictlearningsciences/pal3-core/helpers";
import { LoadStatus } from "../helpers";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { addRecord, loadRecords, saveRecords } from "../store/slices/userData";

export interface UseWithLRStore {
  loadLearnerRecords: (since?: string) => void;
  saveLearnerRecords: () => void;
  addLearnerRecord: (record: Partial<LearnerRecord>) => void;
  getRecords: (filter: (r: LearnerRecord) => boolean) => LearnerRecord[];
  getLatestRecord: (
    filter: (r: LearnerRecord) => boolean
  ) => LearnerRecord | undefined;
}

export function useWithLRStore(): UseWithLRStore {
  const dispatch = useAppDispatch();
  const {
    user,
    accessToken,
    records,
    unsavedRecords,
    recordLoadState,
    recordSaveState,

    activeGoal,
    activeTopic,
  } = useAppSelector((state) => state.userData);

  function loadLearnerRecords(since?: string): void {
    if (accessToken && recordLoadState?.status !== LoadStatus.IN_PROGRESS) {
      dispatch(loadRecords(since || ""));
    }
  }

  function saveLearnerRecords(): void {
    if (
      !accessToken ||
      recordSaveState?.status === LoadStatus.IN_PROGRESS ||
      unsavedRecords.length === 0
    ) {
      return;
    }
    dispatch(saveRecords());
  }

  function addLearnerRecord(r: Partial<LearnerRecord>): void {
    if (!r.objType || !r.verb) {
      return;
    }
    dispatch(
      addRecord({
        id: r.id || uuid(),
        idO: r.idO || "",
        objId: r.objId || "",
        objType: r.objType,
        verb: r.verb,
        sid: r.sid || uuid(),
        timestamp: r.timestamp || getFormattedDate(),
        result: r.result,
        extensions: {
          user: {
            name: user?.name,
            traits: user?.traits,
          },
          goal: {
            id: activeGoal?.id,
            alias: activeGoal?.alias,
            name: activeGoal?.name,
            xapiSubscribers: activeGoal?.xapiSubscribers,
            kcs: activeGoal?.focusList.reduce(
              (acc: KnowledgeComponentRelevance[], focus) => {
                for (const topic of focus.topics) {
                  for (const kc of topic.knowledgeComponents) {
                    if (!acc.find((k) => k.kc === kc.kc)) {
                      acc.push(kc);
                    }
                  }
                }
                return acc;
              },
              []
            ),
          },
          topic: {
            id: activeTopic?.id,
            alias: activeTopic?.alias,
            name: activeTopic?.name,
            kcs: activeTopic?.knowledgeComponents,
          },
          ...(r.extensions || {}),
        },
      })
    );
  }

  function getRecords(filter: (r: LearnerRecord) => boolean): LearnerRecord[] {
    return records.filter((r) => filter(r));
  }

  function getLatestRecord(
    filter: (r: LearnerRecord) => boolean
  ): LearnerRecord | undefined {
    return records.find((rs) => filter(rs));
  }

  return {
    loadLearnerRecords,
    saveLearnerRecords,
    addLearnerRecord,
    getRecords,
    getLatestRecord,
  };
}
